<script>
  // The want page at /u/@<handle>/wantlist (#222; spec
  // docs/superpowers/specs/2026-11-02-wantlist-redesign-design.md). Two
  // tabs: Wants (the default; WantsTab) and People (PeopleTab, with the
  // group picker top right). The page's state, its one read and the owner's
  // writes live in stores/want-page.svelte.js; this view renders them and
  // reads the page again for a new handle or picker selection.
  import { untrack } from 'svelte';
  import { authState } from '../../lib/stores/auth.svelte.js';
  import { wantPage, loadWantPage, resetWantPage } from '../../lib/stores/want-page.svelte.js';
  import WantsTab from './WantsTab.svelte';
  import PeopleTab from './PeopleTab.svelte';
  import GroupPicker from '../layout/GroupPicker.svelte';
  import SignInPrompt from '../shared/SignInPrompt.svelte';

  /** @typedef {'wants' | 'people'} Tab */

  let { handle = 'me' } = $props();

  let tab = $state(/** @type {Tab} */ ('wants'));

  // Each mount is a fresh page: nothing of a previous visit carries over.
  resetWantPage();

  /** @type {Array<{ id: Tab, label: string }>} */
  const TABS = [
    { id: 'wants', label: 'Wants' },
    { id: 'people', label: 'People' },
  ];

  // A new handle or picker selection (the store's `scopeKey`, a string, so
  // an untouched selection never re-reads) reads again; untracked, so the
  // store's own state never re-runs this.
  $effect(() => {
    const h = handle;
    void wantPage.scopeKey;
    if (authState.ready) untrack(() => loadWantPage(h));
  });

  /**
   * The tab list's keys (WAI-ARIA tabs): arrows move between the tabs and
   * select them, Home / End jump to the ends.
   * @param {KeyboardEvent} e
   */
  function onTabKeydown(e) {
    const at = TABS.findIndex((t) => t.id === tab);
    const to =
      e.key === 'ArrowRight'
        ? (at + 1) % TABS.length
        : e.key === 'ArrowLeft'
          ? (at - 1 + TABS.length) % TABS.length
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? TABS.length - 1
              : -1;
    if (to < 0) return;
    e.preventDefault();
    tab = TABS[to].id;
    document.getElementById(`want-tab-${tab}`)?.focus();
  }
</script>

{#snippet tabIcon(/** @type {Tab} */ id)}
  {#if id === 'wants'}
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"
      ><rect x="3" y="5" width="11" height="16" rx="1.5" transform="rotate(-8 8 13)" /><rect
        x="10"
        y="3"
        width="11"
        height="16"
        rx="1.5"
        opacity=".55"
      /></svg
    >
  {:else}
    <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor" aria-hidden="true"
      ><circle cx="9" cy="8" r="4" /><path d="M1 21c0-4.4 3.6-7 8-7s8 2.6 8 7z" /><circle
        cx="17.5"
        cy="7"
        r="3"
      /><path d="M17 12.5c3.6 0 6 2.2 6 6v1h-4.5c0-2.7-.8-5-1.5-7z" /></svg
    >
  {/if}
{/snippet}

<main class="want-page">
  <header class="title-bar">
    {#if !wantPage.loading && !wantPage.signedOut}
      <h1>{wantPage.isSelf ? 'Your want list' : `@${handle}'s want list`}</h1>
    {/if}
  </header>

  {#if wantPage.signedOut}
    <SignInPrompt
      title="Sign in to see want lists"
      text="Want lists are visible to people who share a group."
    />
  {:else if wantPage.loading}
    <p class="status">Loading&hellip;</p>
  {:else if wantPage.notFound}
    <div class="status">
      <h2>Not found</h2>
      <p>This want list doesn't exist or you don't have access to it.</p>
    </div>
  {:else}
    {#if wantPage.reloadFailed}
      <p class="reload-error" role="alert">
        {wantPage.reloadFailed === 'afterWrite'
          ? "Saved, but the list couldn't refresh. Reload the page to see it."
          : "The list couldn't refresh. Reload the page to try again."}
      </p>
    {/if}
    <div class="topbar">
      <div class="tabs" role="tablist" aria-label="View" tabindex="-1" onkeydown={onTabKeydown}>
        {#each TABS as t (t.id)}
          <button
            id="want-tab-{t.id}"
            role="tab"
            aria-selected={tab === t.id}
            aria-controls="want-panel-{t.id}"
            tabindex={tab === t.id ? 0 : -1}
            onclick={() => (tab = t.id)}
          >
            {@render tabIcon(t.id)}
            {t.label}
          </button>
        {/each}
      </div>
      {#if tab === 'wants'}
        <div class="switch" role="group" aria-label="Show cards as">
          <button
            aria-pressed={wantPage.display === 'list'}
            onclick={() => (wantPage.display = 'list')}>List</button
          >
          <button
            aria-pressed={wantPage.display === 'cards'}
            onclick={() => (wantPage.display = 'cards')}>Cards</button
          >
        </div>
      {:else}
        <GroupPicker />
      {/if}
    </div>

    <div
      id="want-panel-wants"
      class="panel"
      role="tabpanel"
      aria-labelledby="want-tab-wants"
      hidden={tab !== 'wants'}
    >
      {#if tab === 'wants'}
        <WantsTab />
      {/if}
    </div>
    <div
      id="want-panel-people"
      class="panel"
      role="tabpanel"
      aria-labelledby="want-tab-people"
      hidden={tab !== 'people'}
    >
      {#if tab === 'people'}
        <PeopleTab
          entries={wantPage.entries}
          failed={wantPage.peopleFailed}
          stale={wantPage.peopleStale}
        />
      {/if}
    </div>
  {/if}
</main>

<style>
  /* border-box: the side padding sits inside the 100% width, or the page is
     32px wider than its column and runs off a phone's screen. */
  .want-page {
    box-sizing: border-box;
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1rem 4rem;
    z-index: 1;
  }
  /* The pinned bar the sidebar's menu button sits on (VISUALS.md "The menu
     button never floats over content"): the title starts past the button
     (44px at 16px/16px) and is centred on it. */
  .title-bar {
    position: sticky;
    top: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    min-height: 76px;
    margin: 0 -1rem 4px;
    padding: 0 1rem 0 68px;
    background: var(--color-bar-bg);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
  }
  .title-bar h1 {
    margin: 0;
    font-size: 1.5rem;
    color: var(--color-text);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .status {
    color: var(--color-text-tertiary);
    text-align: center;
    margin-top: 2rem;
  }
  .status h2 {
    color: var(--color-text);
  }

  .reload-error {
    margin: 0 0 10px;
    font-size: 13px;
    color: var(--color-error);
  }
  .topbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 12px;
  }
  .tabs,
  .switch {
    display: inline-flex;
    padding: 4px;
    border-radius: 12px;
    background: var(--color-accent-bg);
    border: 1px solid var(--color-border);
  }
  .tabs button,
  .switch button {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    min-height: 38px;
    padding: 8px 16px;
    border: none;
    border-radius: 9px;
    background: transparent;
    color: var(--color-text-tertiary);
    font: inherit;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
  }
  .switch button {
    padding: 8px 12px;
    font-size: 14px;
  }
  .tabs button[aria-selected='true'],
  .switch button[aria-pressed='true'] {
    background: var(--color-accent-dark);
    color: #fff;
  }
  .tabs button:active,
  .switch button:active {
    transform: scale(0.98);
  }

  @media (max-width: 768px) {
    .title-bar h1 {
      font-size: 1.375rem;
    }
    .tabs button {
      padding: 8px 12px;
    }
  }
</style>
